import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Loader, AlertCircle, Info } from 'lucide-react';
import { recommendationsService } from '../../services/recommendationsService';
import { eventsService } from '../../services/eventsService';
import WhyThisPopover from './WhyThisPopover';
import SmartContentCard from '../swirlspace/SmartContentCard';

/**
 * PersonalizedSection - Horizontal scrolling section with personalized recommendations
 * 
 * Props:
 * - title: Section title
 * - description: Section description
 * - icon: Lucide icon component
 * - sectionKey: 'forYou' | 'liked' | 'continue' | 'trending'
 * - pageSize: Number of items per page (default: 20)
 * - onItemClick: Callback when item is clicked
 * - onSeeAll: Callback for "See all" link
 * - onItemsLoaded: Callback when items are loaded (for AdaptiveFilters)
 */
export default function PersonalizedSection({
  title,
  description,
  icon: Icon,
  sectionKey,
  pageSize = 20,
  onItemClick,
  onSeeAll,
  onItemsLoaded,
}) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [metadata, setMetadata] = useState(null);
  
  const sectionRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const retryCountRef = useRef(0);
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const loadSection = useCallback(async (pageNum = 1, append = false) => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    setIsLoading(true);
    setError(null);

    try {
      const result = await recommendationsService.getSectionRecommendations(
        sectionKey,
        pageNum,
        pageSize
      );

      if (abortControllerRef.current.signal.aborted) {
        return; // Request was cancelled
      }

      if (append) {
        setItems(prev => [...prev, ...(result.results || [])]);
      } else {
        setItems(result.results || []);
      }

      setHasMore(result.hasMore);
      setMetadata(result.metadata);
      setPage(pageNum);
      retryCountRef.current = 0; // Reset retry count on success

      // Notify parent of loaded items (for AdaptiveFilters)
      if (onItemsLoaded && result.results && result.results.length > 0) {
        onItemsLoaded(result.results);
      }

      // Track impression
      if (result.results && result.results.length > 0) {
        eventsService.trackImpression(
          null,
          `recommendations:${sectionKey}`,
          result.results.flatMap(item => item.tags || []),
          result.results.flatMap(item => item.colors || [])
        );
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        return; // Request was cancelled, ignore
      }

      // Retry with exponential backoff
      if (retryCountRef.current < 2) {
        retryCountRef.current += 1;
        const delay = Math.pow(2, retryCountRef.current) * 1000; // 2s, 4s
        
        setTimeout(() => {
          loadSection(pageNum, append);
        }, delay);
      } else {
        setError(err.message || 'Failed to load recommendations');
        setIsLoading(false);
      }
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [sectionKey, pageSize]);

  useEffect(() => {
    loadSection(1, false);

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadSection]);

  const handleScroll = (direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = 400;
    const targetScroll = direction === 'left' 
      ? container.scrollLeft - scrollAmount
      : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: targetScroll,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  };

  const handleLoadMore = () => {
    if (!isLoading && hasMore) {
      loadSection(page + 1, true);
    }
  };

  const handleItemClick = (item) => {
    if (onItemClick) {
      onItemClick(item);
    }

    // Track click
    eventsService.trackClick(
      item.id,
      `recommendations:${sectionKey}`,
      item.tags || [],
      item.colors || []
    );
  };

  const handleKeyDown = (e, direction) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleScroll(direction);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: prefersReducedMotion ? 0 : 0.3,
        staggerChildren: prefersReducedMotion ? 0 : 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: prefersReducedMotion ? 0 : 20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: prefersReducedMotion ? 0 : 0.2 },
    },
  };

  return (
    <motion.section
      ref={sectionRef}
      className="mb-12"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      aria-label={title}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-neutral-600" />}
          <div>
            <h2 className="text-xl font-semibold text-neutral-900">{title}</h2>
            {description && (
              <p className="text-sm text-neutral-600 mt-1">{description}</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {metadata?.biasUsed && (
            <WhyThisPopover
              biasUsed={metadata.biasUsed}
              biasContribution={items[0]?.biasContribution}
            />
          )}
          
          {onSeeAll && (
            <button
              onClick={() => onSeeAll(sectionKey)}
              className="text-sm text-[#7A47F5] hover:text-[#6A37E5] font-medium"
            >
              See all →
            </button>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg mb-4"
        >
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-sm text-red-800">{error}</p>
          <button
            onClick={() => loadSection(1, false)}
            className="ml-auto text-sm text-red-600 hover:text-red-800 underline"
          >
            Retry
          </button>
        </motion.div>
      )}

      {/* Loading Skeleton */}
      {isLoading && items.length === 0 && (
        <div className="flex gap-4 overflow-hidden">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="flex-shrink-0 w-64 h-48 bg-neutral-100 rounded-lg animate-pulse"
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && items && items.length === 0 && (
        <div className="flex items-center justify-center py-12 text-neutral-500">
          <div className="text-center">
            <Info className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No recommendations available yet.</p>
          </div>
        </div>
      )}

      {/* Content */}
      {items && items.length > 0 && (
        <div className="relative">
          {/* Scroll Buttons */}
          <button
            onClick={() => handleScroll('left')}
            onKeyDown={(e) => handleKeyDown(e, 'left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-all"
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-5 h-5 text-neutral-700" />
          </button>

          <button
            onClick={() => handleScroll('right')}
            onKeyDown={(e) => handleKeyDown(e, 'right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-all"
            aria-label="Scroll right"
          >
            <ChevronRight className="w-5 h-5 text-neutral-700" />
          </button>

          {/* Horizontal Scroll Container */}
          <div
            ref={scrollContainerRef}
            className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide"
            style={{
              scrollBehavior: prefersReducedMotion ? 'auto' : 'smooth',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
            role="list"
            aria-label={`${title} recommendations`}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'ArrowLeft') {
                e.preventDefault();
                handleScroll('left');
              } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                handleScroll('right');
              }
            }}
          >
            <AnimatePresence>
              {(items || []).map((item) => (
                <motion.div
                  key={item.id}
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  className="flex-shrink-0 w-64 relative group"
                  role="listitem"
                >
                  <SmartContentCard
                    item={{
                      id: item.id,
                      title: item.title,
                      url: item.previewUrl,
                      primary_tags: item.tags,
                      primary_color: item.colors[0],
                      auto_tags: item.tags,
                      source_type: item.source_type || 'web_clip',
                      meta: item.meta || {},
                    }}
                    onClick={() => handleItemClick(item)}
                    query={`recommendations:${sectionKey}`}
                    context={sectionKey}
                  />
                  {/* Item-specific WhyThisPopover */}
                  {(item.scoreBreakdown || item.biasContribution || item.matchReasons) && (
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                      <WhyThisPopover
                        item={item}
                        scoreBreakdown={item.scoreBreakdown}
                        biasContribution={item.biasContribution}
                        matchReasons={item.matchReasons}
                      />
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Load More Trigger */}
            {hasMore && (
              <div className="flex-shrink-0 w-64 flex items-center justify-center">
                {isLoading ? (
                  <Loader className="w-6 h-6 animate-spin text-neutral-400" />
                ) : (
                  <button
                    onClick={handleLoadMore}
                    className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-900 border border-neutral-200 rounded-lg hover:border-neutral-300 transition-colors"
                  >
                    Load more
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </motion.section>
  );
}

